import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  TextField,
  Button,
  IconButton,
  Divider,
  CircularProgress,
  Badge,
  Drawer,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ListItemButton,
  Alert,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import MenuIcon from "@mui/icons-material/Menu";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ImageIcon from "@mui/icons-material/Image";
import VideocamIcon from "@mui/icons-material/Videocam";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import AddIcon from "@mui/icons-material/Add";
import { useApi } from "../../../utils/api";
import { useAuth } from "../../../utils/auth";
import { WebSocketService } from "../../../utils/websocket";
import moment from "moment";
import { useUserInfo } from "../../../utils/user";
// Conversation list sidebar
const ConversationList = ({
  conversations,
  selectedConversation,
  onSelectConversation,
  isMobile,
  onClose,
  theme,
  onNewConversationClick,
  onLoadMore,
  hasMore = false,
  loadingMore = false,
}) => {
  const [searchQuery, setSearchQuery] = useState("");

  const filteredConversations = conversations.filter((convo) =>
    convo.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleScroll = (event) => {
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    const isNearBottom = scrollHeight - scrollTop - clientHeight < 120;
    if (isNearBottom && hasMore && !loadingMore) {
      onLoadMore?.();
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Box sx={{ p: 2, display: "flex", gap: 1, alignItems: "center" }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search conversations..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <IconButton
          color="primary"
          onClick={onNewConversationClick}
          sx={{
            backgroundColor: "primary.main",
            color: "white",
            "&:hover": {
              backgroundColor: "primary.dark",
            },
          }}
        >
          <AddIcon />
        </IconButton>
      </Box>
      <Box
        sx={{
          flexGrow: 1,
          overflow: "auto",
          "&::-webkit-scrollbar": {
            width: "8px",
          },
          "&::-webkit-scrollbar-track": {
            background: "#f1f1f1",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "#888",
            borderRadius: "4px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            background: "#555",
          },
        }}
        onScroll={handleScroll}
      >
        <List>
          {filteredConversations.length > 0 ? (
            filteredConversations.map((conversation) => (
              <ListItem
                button
                key={conversation.id}
                sx={{
                  backgroundColor:
                    selectedConversation?.id === conversation.id
                      ? "red"
                      : "transparent",
                  cursor: "pointer",
                  "&:hover": {
                    backgroundColor:
                      selectedConversation?.id === conversation.id
                        ? theme.palette.primary.light
                        : theme.palette.action.hover,
                  },
                }}
                onClick={() => {
                  onSelectConversation(conversation);
                  if (isMobile) onClose();
                }}
              >
                <ListItemText
                  primary={
                    <Box
                      sx={{ display: "flex", justifyContent: "space-between" }}
                    >
                      <Typography
                        sx={{
                          fontWeight: conversation.isRead ? "normal" : "bold",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          color:
                            selectedConversation?.id === conversation.id
                              ? "white"
                              : "inherit",
                        }}
                      >
                        {conversation.name}
                      </Typography>
                      {!conversation.isRead && (
                        <Badge color="primary" variant="dot" sx={{ ml: 1 }} />
                      )}
                    </Box>
                  }
                  secondary={
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        width: "100%",
                        color:
                          selectedConversation?.id === conversation.id
                            ? "white"
                            : "inherit",
                      }}
                    >
                      {conversation.mostRecentMessage?.content
                        ? conversation.mostRecentMessage.content
                        : "No messages yet"}
                    </Typography>
                  }
                />
              </ListItem>
            ))
          ) : (
            <Box sx={{ p: 2, textAlign: "center" }}>
              <Typography color="text.secondary">
                {searchQuery
                  ? "No conversations match your search"
                  : "No conversations yet"}
              </Typography>
            </Box>
          )}
        </List>
        {loadingMore && (
          <Box sx={{ p: 2, display: "flex", justifyContent: "center" }}>
            <CircularProgress size={18} />
          </Box>
        )}
      </Box>
    </Box>
  );
};

// Message component
const Message = ({ message, currentUserId }) => {
  const isCurrentUser = message.senderId === currentUserId;
  const hasMediaAttachment = message.mediaKey && message.mediaType;

  // Function to render media content based on type
  const renderMediaContent = () => {
    if (!hasMediaAttachment) return null;

    // Get media URL (you might need to adjust this based on your storage setup)
    const mediaUrl = `https://trainer-app-videos.s3.us-east-2.amazonaws.com/${message.mediaKey}`;

    if (message.mediaType === "image") {
      return (
        <Box sx={{ mt: 1, mb: 1, maxWidth: "100%" }}>
          <img
            src={mediaUrl}
            alt="Image attachment"
            style={{ maxWidth: "100%", borderRadius: "4px" }}
          />
        </Box>
      );
    } else if (message.mediaType === "video") {
      return (
        <Box sx={{ mt: 1, mb: 1, maxWidth: "100%" }}>
          <video controls style={{ maxWidth: "100%", borderRadius: "4px" }}>
            <source src={mediaUrl} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </Box>
      );
    }

    return (
      <Box sx={{ mt: 1, mb: 1, display: "flex", alignItems: "center" }}>
        <AttachFileIcon sx={{ mr: 1 }} />
        <Typography variant="body2">Attachment</Typography>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: isCurrentUser ? "flex-end" : "flex-start",
        mb: 2,
      }}
    >
      <Paper
        sx={{
          p: 2,
          backgroundColor: isCurrentUser ? "red" : "grey.100",
          color: isCurrentUser ? "white" : "inherit",
          maxWidth: "70%",
          borderRadius: 2,
        }}
      >
        {!isCurrentUser && (
          <Typography variant="subtitle2" color="text.secondary">
            {message.senderName}
          </Typography>
        )}

        {message.content && (
          <Typography variant="body1">{message.content}</Typography>
        )}

        {renderMediaContent()}

        <Typography
          variant="caption"
          color={isCurrentUser ? "white" : "text.secondary"}
          sx={{ display: "block", textAlign: "right", mt: 1 }}
        >
          {moment.unix(message.timestamp).format("h:mm A")}
        </Typography>
      </Paper>
    </Box>
  );
};

// New Conversation Dialog Component
const NewConversationDialog = ({
  open,
  onClose,
  clients,
  conversations,
  currentUserId,
  onConversationCreated,
  startConversation,
  getClients,
  getConversationParticipants,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [clientSearchQuery, setClientSearchQuery] = useState("");
  const [availableClients, setAvailableClients] = useState([]);

  useEffect(() => {
    if (open) {
      loadClients();
    }
  }, [open]);

  const loadClients = async () => {
    try {
      setLoading(true);
      const clientsData = await getClients();
      // Filter out clients that already have conversations with the coach
      const existingConversationClientIds = new Set();

      for (const conv of conversations) {
        try {
          const participants = await getConversationParticipants(conv.id);
          // Check if this conversation has exactly 2 participants (coach + client)
          // and one of them is a client
          if (participants.length === 2) {
            const participantIds = participants.map((p) => p.participantId);
            // Find the client ID (the one that's not the current user)
            const clientId = participantIds.find((id) => id !== currentUserId);
            if (clientId) {
              existingConversationClientIds.add(clientId);
            }
          }
        } catch (err) {
          console.error(
            `Error fetching participants for conversation ${conv.id}:`,
            err
          );
          // Continue with other conversations even if one fails
        }
      }

      // Filter clients that don't have conversations yet
      const filteredClients = clientsData.filter(
        (client) => !existingConversationClientIds.has(client.id)
      );

      setAvailableClients(filteredClients);
      setError(null);
    } catch (err) {
      console.error("Error loading clients:", err);
      setError("Failed to load clients. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSelectClient = async (client) => {
    try {
      setLoading(true);
      setError(null);

      // Create a new conversation with the selected client
      // Include both the current user (coach) and the client in participantIds
      const conversationData = {
        participantIds: [currentUserId, client.id],
        name: client.name, // Use client name as conversation name
      };

      const newConversation = await startConversation(conversationData);

      // Call the callback to handle the new conversation
      if (onConversationCreated) {
        onConversationCreated(newConversation);
      }

      onClose();
    } catch (err) {
      console.error("Error creating conversation:", err);
      setError(
        err.message || "Failed to create conversation. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const filteredClients = availableClients.filter(
    (client) =>
      client.name.toLowerCase().includes(clientSearchQuery.toLowerCase()) ||
      client.email?.toLowerCase().includes(clientSearchQuery.toLowerCase())
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Start New Conversation</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <TextField
          fullWidth
          size="small"
          placeholder="Search clients..."
          value={clientSearchQuery}
          onChange={(e) => setClientSearchQuery(e.target.value)}
          sx={{ mb: 2 }}
        />
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
            <CircularProgress />
          </Box>
        ) : filteredClients.length > 0 ? (
          <List>
            {filteredClients.map((client) => (
              <ListItem key={client.id} disablePadding>
                <ListItemButton onClick={() => handleSelectClient(client)}>
                  <ListItemText
                    primary={client.name}
                    secondary={client.email}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        ) : (
          <Box sx={{ p: 2, textAlign: "center" }}>
            <Typography color="text.secondary">
              {clientSearchQuery
                ? "No clients match your search"
                : availableClients.length === 0
                ? "All clients already have conversations"
                : "No clients available"}
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
};

// Main messaging component
const Messaging = () => {
  const {
    getConversations,
    getMessages,
    sendMessage: apiSendMessage,
    markConversationRead,
    getConversationParticipants,
    putSignedUrl,
    startConversation,
    getClients,
  } = useApi();
  const { getUserId } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(true);
  const [mediaAttachment, setMediaAttachment] = useState(null);
  const [attachmentType, setAttachmentType] = useState(null);
  const [webSocketService, setWebSocketService] = useState(null);
  const [newConversationDialogOpen, setNewConversationDialogOpen] =
    useState(false);
  const [nextConversationItem, setNextConversationItem] = useState(null);
  const [hasMoreConversations, setHasMoreConversations] = useState(true);
  const [loadingMoreConversations, setLoadingMoreConversations] =
    useState(false);
  const fileInputRef = useRef(null);
  const messagesEndRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const currentUserId = getUserId();
  const { name, id: userId } = useUserInfo();
  const CONVERSATIONS_PAGE_SIZE = 40;

  const normalizeConversation = (conversation = {}) => {
    const content =
      conversation.mostRecentMessage?.content ||
      conversation.lastMessage ||
      conversation.lastMessageContent ||
      "";
    const timestamp =
      conversation.mostRecentMessage?.timestamp ||
      conversation.lastMessageTimestamp ||
      conversation.lastMessageTime ||
      null;

    return {
      ...conversation,
      mostRecentMessage: content
        ? {
            ...conversation.mostRecentMessage,
            content,
            timestamp,
          }
        : null,
      lastMessage: content,
      lastMessageTimestamp: timestamp,
    };
  };

  // Initialize WebSocket connection
  useEffect(() => {
    let ws = null;

    if (userId && selectedConversation?.id) {
      ws = new WebSocketService(userId, selectedConversation.id);
      setWebSocketService(ws);
      ws.connect();

      // Subscribe to new messages
      const handleNewMessage = (message) => {
        console.log("New message received in handler:", message);
        if (message.conversationId === selectedConversation?.id) {
          // Check if message already exists and handle temporary message replacement
          setMessages((prev) => {
            // Check if message already exists in the list
            const isDuplicate = prev.some((m) => m.id === message.id);
            if (isDuplicate) {
              console.log("Message already exists in the list, ignoring");
              return prev;
            }

            // Check if this is a real message replacing a temporary optimistic message
            // Look for a temporary message with matching content, sender, and similar timestamp
            const tempMessageIndex = prev.findIndex(
              (m) =>
                m.id.startsWith("temp-") &&
                m.senderId === message.senderId &&
                m.content === message.content &&
                m.mediaKey === message.mediaKey &&
                Math.abs(m.timestamp - message.timestamp) < 5 // Within 5 seconds
            );

            if (tempMessageIndex !== -1) {
              // Replace the temporary message with the real one
              const updated = [...prev];
              updated[tempMessageIndex] = message;
              return updated;
            }

            // Otherwise, add the new message
            return [...prev, message];
          });

          // Update conversation's last message
          setConversations((prev) =>
            prev.map((convo) =>
              convo.id === message.conversationId
                ? {
                    ...convo,
                    lastMessage:
                      message.content || `[${message.mediaType || "Media"}]`,
                    lastMessageTimestamp: message.timestamp,
                    mostRecentMessage: {
                      content:
                        message.content || `[${message.mediaType || "Media"}]`,
                      timestamp: message.timestamp,
                    },
                  }
                : convo
            )
          );
        }
      };

      ws.subscribe("newMessage", handleNewMessage);

      return () => {
        // Cleanup previous WebSocket connection
        if (ws) {
          ws.unsubscribe("newMessage", handleNewMessage);
          ws.disconnect();
        }
      };
    }
  }, [userId, selectedConversation?.id]);

  // Fetch messages when selected conversation changes
  useEffect(() => {
    const fetchMessages = async () => {
      if (!selectedConversation) return;

      setLoadingMessages(true);
      try {
        const response = await getMessages(selectedConversation.id);
        setMessages(response);
        await markConversationRead(selectedConversation.id);

        // Update the read status in the conversations list
        setConversations((prev) =>
          prev.map((convo) =>
            convo.id === selectedConversation.id
              ? { ...convo, isRead: true }
              : convo
          )
        );
      } catch (error) {
        console.error("Error fetching messages:", error);
      } finally {
        setLoadingMessages(false);
      }
    };

    fetchMessages();
  }, [selectedConversation, getMessages, markConversationRead]);

  // Scroll to bottom whenever messages change
  useEffect(() => {
    // Use a small timeout to ensure the DOM has updated
    const scrollTimeout = setTimeout(() => {
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
      }
    }, 100);

    return () => clearTimeout(scrollTimeout);
  }, [messages]);

  // Fetch conversations (paginated)
  const fetchConversations = async ({
    append = false,
    nextToken = null,
    selectConversationId = null,
  } = {}) => {
    const resultSummary = { normalized: [], next: null };
    try {
      if (append) {
        if (loadingMoreConversations || !hasMoreConversations) {
          return resultSummary;
        }
        setLoadingMoreConversations(true);
      } else {
        setLoading(true);
      }

      console.log("Fetching conversations for userId:", userId, {
        append,
        nextToken,
      });
      if (!userId) {
        console.warn("No userId available, skipping conversation fetch");
        setLoading(false);
        return resultSummary;
      }
      const response = await getConversations({
        limit: CONVERSATIONS_PAGE_SIZE,
        nextToken,
      });
      console.log("Conversations fetched - raw response:", response);

      let conversationsList = [];
      let nextFromResponse = null;
      let hasMore = false;
      if (Array.isArray(response)) {
        conversationsList = response;
      } else if (response && typeof response === "object") {
        conversationsList =
          response.conversations || response.data || response.items || [];
        nextFromResponse = response.nextToken ?? null;
        hasMore = response.hasMore ?? Boolean(nextFromResponse);
      }

      console.log("Processed conversations list:", conversationsList);
      console.log("Number of conversations:", conversationsList.length);

      const normalized = conversationsList.map(normalizeConversation);
      resultSummary.normalized = normalized;
      resultSummary.next = nextFromResponse;
      setNextConversationItem(nextFromResponse);
      setHasMoreConversations(hasMore);

      let updatedList = [];
      setConversations((prev) => {
        const combined = append ? [...prev, ...normalized] : normalized;
        const uniqueById = Array.from(
          new Map(combined.map((c) => [c.id, c])).values()
        );
        updatedList = uniqueById;
        return uniqueById;
      });

      if (selectConversationId) {
        const match =
          updatedList.find((c) => c.id === selectConversationId) || null;
        if (match) {
          setSelectedConversation(match);
        }
      } else if (!selectedConversation && updatedList.length > 0 && !append) {
        setSelectedConversation(updatedList[0]);
      }
    } catch (error) {
      console.error("Error fetching conversations:", error);
      console.error("Error details:", error.message, error.stack);
      if (!append) {
        setConversations([]);
      }
    } finally {
      if (append) {
        setLoadingMoreConversations(false);
      } else {
        setLoading(false);
      }
    }
    return resultSummary;
  };

  useEffect(() => {
    if (userId) {
      fetchConversations();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]); // Run when userId is available

  const handleNewConversationCreated = async (newConversation) => {
    // Refresh conversations list from server to ensure we have the latest data
    try {
      const { normalized } = await fetchConversations({
        append: false,
        nextToken: null,
        selectConversationId: newConversation?.id,
      });

      if (newConversation?.id) {
        const createdConv = normalized.find(
          (conv) => conv.id === newConversation.id
        );
        if (createdConv) {
          setSelectedConversation(createdConv);
          return;
        }
      }

      if (!selectedConversation && normalized.length > 0) {
        setSelectedConversation(normalized[0]);
      }
    } catch (error) {
      console.error("Error refreshing conversations:", error);
      // Fallback: add the conversation locally
      const normalized = normalizeConversation(newConversation);
      setConversations((prev) => [normalized, ...prev]);
      setSelectedConversation(normalized);
    }
  };

  const handleLoadMoreConversations = () => {
    if (
      !hasMoreConversations ||
      loadingMoreConversations ||
      !nextConversationItem
    )
      return;
    fetchConversations({
      append: true,
      nextToken: nextConversationItem,
    });
  };

  const handleAttachmentClick = (type) => {
    setAttachmentType(type);
    fileInputRef.current.click();
  };

  const handleFileSelected = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check if file type matches the expected type
    if (attachmentType === "image" && !file.type.startsWith("image/")) {
      alert("Please select an image file");
      return;
    }

    if (attachmentType === "video" && !file.type.startsWith("video/")) {
      alert("Please select a video file");
      return;
    }

    try {
      // Generate a unique key for the file
      const timestamp = Date.now();
      const fileName = `${timestamp}-${file.name}`;
      const key = `public/${userId}/${fileName}`;

      // Get signed URL for upload
      const { uploadUrl } = await putSignedUrl(key);

      // Upload the file to S3
      const response = await fetch(uploadUrl, {
        method: "PUT",
        body: file,
        headers: {
          "Content-Type": file.type,
        },
      });

      if (!response.ok) {
        throw new Error("Upload failed");
      }

      setMediaAttachment({
        file,
        type: attachmentType,
        name: file.name,
        key: key, // Store the S3 key for later use
      });

      // Reset file input
      e.target.value = null;
    } catch (error) {
      console.error("Error uploading file:", error);
      alert("Failed to upload file. Please try again.");
    }
  };

  const removeAttachment = () => {
    setMediaAttachment(null);
    setAttachmentType(null);
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if ((!newMessage.trim() && !mediaAttachment) || !selectedConversation)
      return;

    try {
      let mediaKey = null;
      let mediaType = null;

      // If we have a media attachment, use the stored key
      if (mediaAttachment) {
        mediaKey = mediaAttachment.key;
        mediaType = mediaAttachment.type;
      }

      const messageData = {
        action: "sendMessage",
        conversationId: selectedConversation.id,
        content: newMessage.trim() || null,
        mediaKey,
        mediaType,
        userId: currentUserId,
        senderName: name || "Unknown User",
      };

      // Create optimistic message to show immediately
      const tempMessageId = `temp-${Date.now()}`;
      const timestamp = Math.floor(Date.now() / 1000);
      const optimisticMessage = {
        id: tempMessageId,
        conversationId: selectedConversation.id,
        senderId: currentUserId,
        senderName: name || "You",
        content: newMessage.trim() || null,
        mediaKey,
        mediaType,
        timestamp,
      };

      // Add optimistic message to UI immediately
      setMessages((prev) => [...prev, optimisticMessage]);

      // Try to send via WebSocket first
      try {
        await webSocketService?.send(messageData);
      } catch (error) {
        console.error(
          "Failed to send via WebSocket, falling back to HTTP:",
          error
        );
        // Fallback to HTTP if WebSocket fails
        await apiSendMessage(selectedConversation.id, {
          content: newMessage.trim() || null,
          mediaKey,
          mediaType,
          userId: currentUserId,
        });
      }

      // Clear the input and attachment
      setNewMessage("");
      setMediaAttachment(null);

      // Update the conversation's last message in the list
      setConversations((prev) =>
        prev.map((convo) =>
          convo.id === selectedConversation.id
            ? {
                ...convo,
                lastMessage: newMessage.trim() || `[${mediaType || "Media"}]`,
                lastMessageTimestamp: Math.floor(Date.now() / 1000),
                mostRecentMessage: {
                  content: newMessage.trim() || `[${mediaType || "Media"}]`,
                  timestamp: Math.floor(Date.now() / 1000),
                },
              }
            : convo
        )
      );
    } catch (error) {
      console.error("Error sending message:", error);
    }
  };

  const drawerWidth = 300;

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "calc(100vh - 64px)",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        width: "100vw",
        height: "calc(100vh - 64px)",
        position: "fixed",
        top: 64,
        left: 0,
        overflow: "hidden",
      }}
    >
      {/* Conversation List Drawer */}
      <Drawer
        variant={isMobile ? "temporary" : "permanent"}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            boxSizing: "border-box",
            position: "relative",
            height: "100%",
          },
        }}
      >
        <ConversationList
          conversations={conversations}
          selectedConversation={selectedConversation}
          onSelectConversation={setSelectedConversation}
          isMobile={isMobile}
          onClose={() => setDrawerOpen(false)}
          theme={theme}
          onNewConversationClick={() => setNewConversationDialogOpen(true)}
          onLoadMore={handleLoadMoreConversations}
          hasMore={hasMoreConversations}
          loadingMore={loadingMoreConversations}
        />
      </Drawer>

      {/* Messages Area */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          height: "100%",
          width: { md: `calc(100% - ${drawerWidth}px)` },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            p: 2,
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          {isMobile && (
            <IconButton
              edge="start"
              onClick={() => setDrawerOpen(true)}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
          )}
          <Typography variant="h6">
            {selectedConversation
              ? selectedConversation.name
              : "Select a conversation"}
          </Typography>
        </Box>

        {/* Messages Container */}
        <Box
          sx={{
            flexGrow: 1,
            p: 2,
            overflow: "auto",
            backgroundColor: "grey.50",
            display: "flex",
            flexDirection: "column",
            "&::-webkit-scrollbar": {
              width: "8px",
            },
            "&::-webkit-scrollbar-track": {
              background: "#f1f1f1",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "#888",
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              background: "#555",
            },
          }}
          ref={(el) => {
            if (el && !loadingMessages && messages.length > 0) {
              el.scrollTop = el.scrollHeight;
            }
          }}
        >
          {loadingMessages ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
              }}
            >
              <CircularProgress />
            </Box>
          ) : selectedConversation ? (
            messages.length > 0 ? (
              <>
                <Box sx={{ flexGrow: 1 }} />
                {messages.map((message) => (
                  <Message
                    key={message.id}
                    message={message}
                    currentUserId={currentUserId}
                  />
                ))}
                <div ref={messagesEndRef} />
              </>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                }}
              >
                <Typography color="text.secondary">
                  No messages in this conversation yet. Send the first message!
                </Typography>
              </Box>
            )
          ) : (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
              }}
            >
              <Typography color="text.secondary">
                Select a conversation to view messages
              </Typography>
            </Box>
          )}
        </Box>

        {/* Message Input */}
        {selectedConversation && (
          <Box
            component="form"
            onSubmit={handleSendMessage}
            sx={{
              p: 2,
              backgroundColor: "background.paper",
              borderTop: "1px solid",
              borderColor: "divider",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Media attachment preview */}
            {mediaAttachment && (
              <Box
                sx={{
                  mb: 2,
                  p: 1,
                  display: "flex",
                  alignItems: "center",
                  bgcolor: "grey.100",
                  borderRadius: 1,
                }}
              >
                {mediaAttachment.type === "image" ? (
                  <ImageIcon sx={{ mr: 1 }} />
                ) : (
                  <VideocamIcon sx={{ mr: 1 }} />
                )}
                <Typography variant="body2" sx={{ flexGrow: 1, mr: 1 }}>
                  {mediaAttachment.name}
                </Typography>
                <IconButton size="small" onClick={removeAttachment}>
                  <ArrowBackIcon fontSize="small" />
                </IconButton>
              </Box>
            )}

            {/* Input area */}
            <Box sx={{ display: "flex" }}>
              <Box sx={{ display: "flex", mr: 1 }}>
                <IconButton
                  size="small"
                  onClick={() => handleAttachmentClick("image")}
                >
                  <ImageIcon />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => handleAttachmentClick("video")}
                >
                  <VideocamIcon />
                </IconButton>
                <input
                  ref={fileInputRef}
                  type="file"
                  style={{ display: "none" }}
                  onChange={handleFileSelected}
                  accept={attachmentType === "image" ? "image/*" : "video/*"}
                />
              </Box>
              <TextField
                fullWidth
                placeholder="Type a message..."
                variant="outlined"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                sx={{ mr: 1 }}
              />
              <Button
                type="submit"
                variant="contained"
                endIcon={<SendIcon />}
                disabled={!newMessage.trim() && !mediaAttachment}
              >
                Send
              </Button>
            </Box>
          </Box>
        )}
      </Box>

      {/* New Conversation Dialog */}
      <NewConversationDialog
        open={newConversationDialogOpen}
        onClose={() => setNewConversationDialogOpen(false)}
        clients={[]}
        conversations={conversations}
        currentUserId={currentUserId}
        onConversationCreated={handleNewConversationCreated}
        startConversation={startConversation}
        getClients={getClients}
        getConversationParticipants={getConversationParticipants}
      />
    </Box>
  );
};

export default Messaging;
